import { useMemo, useRef, useState } from "react";
import { boundedLimit } from "../../lib/decisionScales.js";
import HoverTooltip from "../HoverTooltip.jsx";
import { CATEGORY_PALETTE, KernelEmpty, useElementSize } from "./shared.jsx";

export const kind = "relations";

const ROW_H = 18;
const HEAD_H = 20;
const PAD = 6;
const DOT_X = 6;
const MIN_COL_W = 118;
const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

function shortLabel(node) {
    const label = node.label || "";
    if (label) return label.length > 18 ? `${label.slice(0, 17)}\u2026` : label;
    const id = String(node.id);
    return id.length > 12 ? `${id.slice(0, 8)}\u2026${id.slice(-3)}` : id;
}

// Accepts { nodes, links } or a bare array of links (nodes derived, one
// group). Nodes are grouped into columns in `spec.groups` order, else
// first seen; within a column highlighted nodes come first, then the
// best connected; each column is capped and says how many more it holds.
export function layoutRelations(spec, data) {
    const raw = Array.isArray(data) ? { nodes: [], links: data } : (data && typeof data === "object" ? data : { nodes: [], links: [] });
    const links = (Array.isArray(raw.links) ? raw.links : []).filter((l) => l && l.source != null && l.target != null && String(l.source) !== String(l.target));
    const byId = new Map();
    for (const n of Array.isArray(raw.nodes) ? raw.nodes : []) {
        if (!n || n.id == null) continue;
        byId.set(String(n.id), { id: String(n.id), group: String(n.group || "node"), label: n.label == null ? "" : String(n.label), note: n.note == null ? "" : String(n.note), highlight: Boolean(n.highlight), degree: 0 });
    }
    for (const l of links) {
        for (const id of [String(l.source), String(l.target)]) {
            if (!byId.has(id)) byId.set(id, { id, group: "node", label: "", note: "", highlight: false, degree: 0 });
            byId.get(id).degree += 1;
        }
    }
    const order = Array.isArray(spec.groups) && spec.groups.length ? spec.groups.map(String) : [];
    for (const n of byId.values()) if (!order.includes(n.group)) order.push(n.group);
    const perGroup = boundedLimit(spec.maxPerGroup, 10, 20, 3);
    const totals = spec.groupTotals && typeof spec.groupTotals === "object" ? spec.groupTotals : {};
    const columns = [];
    const shown = new Map();
    for (const group of order) {
        const members = [...byId.values()].filter((n) => n.group === group)
            .sort((a, b) => (Number(b.highlight) - Number(a.highlight)) || (b.degree - a.degree) || (a.label || a.id).localeCompare(b.label || b.id));
        if (!members.length) continue;
        const picked = members.slice(0, perGroup);
        const total = Number.isFinite(Number(totals[group])) ? Math.max(Number(totals[group]), members.length) : members.length;
        const column = { group, total, omitted: total - picked.length, nodes: picked, color: CATEGORY_PALETTE[columns.length % CATEGORY_PALETTE.length] };
        picked.forEach((n, row) => shown.set(n.id, { column: columns.length, row }));
        columns.push(column);
    }
    const edges = [];
    const seen = new Set();
    for (const l of links) {
        const a = String(l.source);
        const b = String(l.target);
        if (!shown.has(a) || !shown.has(b)) continue;
        const key = a < b ? `${a}|${b}` : `${b}|${a}`;
        if (seen.has(key)) continue;
        seen.add(key);
        edges.push({ a, b, kind: l.kind == null ? "" : String(l.kind), hot: byId.get(a).highlight || byId.get(b).highlight });
    }
    edges.sort((x, y) => Number(y.hot) - Number(x.hot));
    return { columns, edges: edges.slice(0, 240), shown, byId, rows: Math.max(1, ...columns.map((c) => c.nodes.length + (c.omitted ? 1 : 0))) };
}

export function Component({ spec, data }) {
    const wrapperRef = useRef(null);
    const { width: measured } = useElementSize(wrapperRef);
    const [hover, setHover] = useState(null);
    const layout = useMemo(() => layoutRelations(spec || {}, data), [spec, data]);
    if (!layout.columns.length) return <KernelEmpty message={spec?.empty || "No relations to draw"} />;
    const available = measured > 0 ? measured : 640;
    const colW = Math.max(MIN_COL_W, Math.floor((available - PAD * 2) / layout.columns.length));
    const width = PAD * 2 + colW * layout.columns.length;
    const height = HEAD_H + PAD * 2 + layout.rows * ROW_H;
    const pos = (id) => {
        const at = layout.shown.get(id);
        return { x: PAD + at.column * colW + DOT_X, y: HEAD_H + PAD + at.row * ROW_H + 9 };
    };
    return (
        <div>
            {spec.title ? <div className="mb-[4px] text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted)]">{spec.title}</div> : null}
            <div ref={wrapperRef} className="w-full overflow-x-auto" onMouseLeave={() => setHover(null)}>
                <svg width={width} height={height} role="img" aria-label={spec.title || "Relations"} style={{ display: "block" }}>
                    {layout.edges.map((e) => {
                        const pa = pos(e.a);
                        const pb = pos(e.b);
                        const leftColor = layout.columns[layout.shown.get(pa.x <= pb.x ? e.a : e.b).column].color;
                        return <line key={`${e.a}|${e.b}`} x1={pa.x} y1={pa.y} x2={pb.x} y2={pb.y} stroke={e.hot ? leftColor : "var(--line-strong)"} strokeOpacity={e.hot ? 0.7 : 0.35} strokeWidth={e.hot ? 1.1 : 0.8} />;
                    })}
                    {layout.columns.map((col, c) => (
                        <g key={col.group}>
                            <text x={PAD + c * colW} y={HEAD_H - 7} className="fill-[color:var(--muted-soft)]" style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: "0.14em" }}>
                                {col.group} {col.total}
                            </text>
                            {col.nodes.map((n) => {
                                const { x, y } = pos(n.id);
                                return (
                                    <g key={n.id} onMouseEnter={(ev) => setHover({ node: n, rect: ev.currentTarget.getBoundingClientRect() })}>
                                        <rect x={x - DOT_X} y={y - 9} width={colW - 4} height={ROW_H} fill="transparent" />
                                        <circle cx={x} cy={y} r={2.5} fill={n.highlight ? col.color : "var(--panel)"} stroke={col.color} strokeWidth={1} />
                                        <text x={x + 8} y={y + 3} className={n.highlight ? "fill-[color:var(--ink)]" : "fill-[color:var(--muted)]"} style={{ fontSize: 9.5, fontFamily: MONO }}>
                                            {shortLabel(n)}
                                        </text>
                                    </g>
                                );
                            })}
                            {col.omitted ? (
                                <text x={PAD + c * colW + DOT_X + 8} y={HEAD_H + PAD + col.nodes.length * ROW_H + 12} className="fill-[color:var(--muted-soft)]" style={{ fontSize: 9, fontFamily: MONO }}>
                                    +{col.omitted} more
                                </text>
                            ) : null}
                        </g>
                    ))}
                </svg>
            </div>
            {hover ? (
                <HoverTooltip anchorRect={hover.rect} placement="top" wide label={<span className="font-mono text-[10px]">{hover.node.id}{hover.node.label ? ` "${hover.node.label}"` : ""}{hover.node.note ? ` [${hover.node.note}]` : ""}{hover.node.highlight ? " new" : ""}</span>} />
            ) : null}
            {spec.caption ? <div className="mt-[3px] text-[9.5px] text-[color:var(--muted-soft)]">{spec.caption}</div> : null}
        </div>
    );
}
